/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-06 10:30:50                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2024-11-06 10:30:50                               *
 *****************************************************************************/

import { Chip } from "@material-tailwind/react";
import { format } from "date-fns";
import { t } from "i18next";
import { getFilterBadgeStyle } from "./filterStyles";

export interface FilterDisplayProps {
  type: string;
  value: any;
  onClear: (value: any) => Promise<void>;
  size?: "sm" | "md" | "lg";
  entity?:
    | "workorder"
    | "replenishment"
    | "quotation"
    | "customer"
    | "service"
    | "prospect"
    | "user"
    | "part";
}

/**
 * FilterDisplay: Composant d'affichage d'un filtre actif sous forme de badge pastel
 * Aligné avec la charte graphique et la palette statusColors (fond 100, croix 400, texte 800)
 */
const FilterDisplay = (props: FilterDisplayProps) => {
  const size = props.size ? props.size : "md";
  const textSize = size === "sm" ? "xs" : size === "md" ? "xs" : "base";

  /**
   * getFilterValue: formatte la valeur affichée dans le badge selon le type de filtre
   */
  const getFilterValue = (value: any): string => {
    if (value === undefined || value === null) {
      return "";
    }

    // Traitement commun pour les objets de type date
    const formatDateObject = (dateObj: any): string => {
      if (!dateObj || typeof dateObj !== "object") return String(dateObj);

      const dateFormat = dateObj.withTime ? "dd/MM/yyyy - HH:mm:ss" : "dd/MM/yyyy";

      // Strip the Z to force Javascript to parse the exact digits as Local Time
      const getLocalDate = (dateVal: string | Date | any) => {
        if (!dateVal) return new Date();
        const dateStr =
          typeof dateVal === "string"
            ? dateVal
            : dateVal instanceof Date
            ? dateVal.toISOString()
            : String(dateVal);
        const localStr =
          typeof dateStr === "string" && dateStr.endsWith("Z") ? dateStr.slice(0, -1) : dateStr;
        return new Date(localStr);
      };

      let dateStr = "";
      if (dateObj.start) {
        dateStr += `${t("common:from", "From")} ${format(getLocalDate(dateObj.start), dateFormat)}`;
      }
      if (dateObj.start && dateObj.end) {
        dateStr += " - ";
      }
      if (dateObj.end) {
        dateStr += `${t("common:to", "To")} ${format(getLocalDate(dateObj.end), dateFormat)}`;
      }
      return dateStr || t("common:dateRange");
    };

    switch (props.type) {
      case "disabled":
        return t("common:disabled", "Disabled");
      case "label":
        return String(value);
      case "ref":
        return String(value);
      case "odooNotSynced":
        return t("common:odooNotSynced");
      case "missingPurchaseOrder":
        return t("workorder:filters:missingPurchaseOrder:badge");
      case "purchaseOrderMandatory":
        return t("client:filters:purchaseOrderMandatory");
      case "externalId":
        return `${t("common:odooExternalId")}: ${value}`;
      case "qrCode":
        return `${t("workorder:filters:qrCode:badge")}: ${value}`;
      case "customer":
      case "name":
        if (typeof value === "object" && value !== null) {
          if (value.name) return value.name;
          if (value.firstName && value.lastName) return `${value.firstName} ${value.lastName}`;
          if (value.firstname && value.lastname) return `${value.firstname} ${value.lastname}`;
          if (value.label) return value.label;
          return String(value.id || "Client");
        }
        return String(value);
      case "contact":
        if (typeof value === "object" && value !== null) {
          if (value.firstname && value.lastname) return `${value.firstname} ${value.lastname}`;
          if (value.firstName && value.lastName) return `${value.firstName} ${value.lastName}`;
          return String(value.name || value.email || "Contact");
        }
        return String(value);
      case "technician":
      case "createdFor":
        if (typeof value === "object" && value !== null) {
          if (value.firstname && value.lastname) return `${value.firstname} ${value.lastname}`;
          if (value.firstName && value.lastName) return `${value.firstName} ${value.lastName}`;
          if (value.name) return value.name;
          return String(value.id || "Technicien");
        }
        return String(value);
      case "van":
        return (
          value.name ||
          (typeof value === "object" && value.label) ||
          (typeof value === "string" ? value : "Van")
        );
      case "status": {
        const entity = props.entity || "workorder";
        const statusKey = String(value).toLowerCase();
        return t(`${entity}:status.${statusKey}`);
      }
      case "safetyDiagStatus": {
        const val = String(value).toUpperCase();
        if (val === "VALID") return t("workorder:filters:safetyDiag:valid", "Valide");
        if (val === "INVALID") return t("workorder:filters:safetyDiag:invalid", "Invalide");
        return String(value);
      }
      case "priority":
        return t(`replenishment:priority.${value}`);
      case "workorderType":
        return String(value);
      case "workorderOrigin":
        return String(value);
      case "dateRange":
      case "date":
      case "scheduledDate":
      case "visitDate":
      case "visitScheduled":
      case "startDate":
      case "endDate":
        return formatDateObject(value);
      case "scheduled":
      case "realTime":
        if (typeof value === "object" && (value.start || value.end)) {
          return formatDateObject(value);
        }
        return value.start && value.end
          ? format(
              new Date(
                typeof value.start === "string" && value.start.endsWith("Z")
                  ? value.start.slice(0, -1)
                  : value.start
              ),
              "dd/MM/yyyy HH:mm"
            ) +
              " - " +
              format(
                new Date(
                  typeof value.end === "string" && value.end.endsWith("Z")
                    ? value.end.slice(0, -1)
                    : value.end
                ),
                "dd/MM/yyyy HH:mm"
              )
          : String(value);
      case "type": {
        const typeValue = String(value).toUpperCase();
        if (typeValue === "ACTIVE" || typeValue === "INACTIVE" || typeValue === "PROSPECT") {
          return t(`client:type.${typeValue.toLowerCase()}`);
        }
        if (typeValue === "SERVICE") return "Service";
        if (typeValue === "BOOKING_FEES") return "Booking Fees";
        if (typeValue === "COMBO") return "Combo";
        return String(value);
      }
      default:
        if (typeof value === "object" && value !== null) {
          if (value.start || value.end) {
            return formatDateObject(value);
          }
          if (value.firstName && value.lastName) {
            return `${value.firstName} ${value.lastName}`;
          }
          if (value.firstname && value.lastname) {
            return `${value.firstname} ${value.lastname}`;
          }
          if (value.name) {
            return value.name;
          }
          if (value.id) {
            return "ID: " + value.id;
          }
          if (value.toString() === "[object Object]") {
            const keys = Object.keys(value);
            if (keys.includes("date") || keys.includes("day")) {
              return formatDateObject(value);
            }
            return t("common:filter");
          }
          return String(value);
        }
        return String(value);
    }
  };

  /**
   * getChipStyle: style inline du chip (fond soft 100, texte 800, couleur croix 400)
   */
  const getChipStyle = (value?: any): React.CSSProperties => {
    const badgeStyle = getFilterBadgeStyle(props.type, value, props.entity);
    return {
      backgroundColor: badgeStyle.soft,
      color: badgeStyle.text,
      ["--chip-close" as string]: badgeStyle.mid,
    };
  };

  /**
   * getChipClassName: classes du chip
   */
  const getChipClassName = (): string => {
    return `w-fit h-fit rounded-full py-1 text-${textSize} flex-shrink-0 normal-case [&>button]:!text-[var(--chip-close)]`;
  };

  const renderFilterChips = () => {
    // Si la valeur est un tableau (multi-sélection)
    if (Array.isArray(props.value)) {
      return (
        <div className="flex flex-wrap gap-2">
          {props.value.map((value, index) => (
            <Chip
              key={index}
              variant="ghost"
              size={size}
              open={true}
              onClose={async () => {
                const newValues = props.value.filter((_: any, i: number) => i !== index);
                await props.onClear(newValues.length === 0 ? null : newValues);
              }}
              value={getFilterValue(value)}
              className={getChipClassName()}
              style={getChipStyle(value)}
            />
          ))}
        </div>
      );
    }

    // Valeur simple
    const values = [props.value];

    return (
      <div className="flex flex-wrap gap-2">
        {values.map((value, index) => (
          <Chip
            key={index}
            variant="ghost"
            size={size}
            open={true}
            onClose={async () => {
              await props.onClear(null);
            }}
            value={getFilterValue(value)}
            className={getChipClassName()}
            style={getChipStyle(value)}
          />
        ))}
      </div>
    );
  };

  return renderFilterChips();
};

export default FilterDisplay;
